import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useVideoContext } from '../hooks';
import { UserRole } from '@uplift/types';
import { ROUTING_CONFIG, TimeFormats } from '@uplift/constants';

import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { findParty } from '../utils';
import { MediaSetupComponent } from './components/media-setup';
import { InfoComponent } from './components/info';
import { AllowDevicesModal } from './components/allow-devices-modal';
import { UnableStartSessionModal } from './components/unable-start-session-modal';
import { BetterInPortraitModal } from '../modals';
import { Redirect } from 'react-router-dom';
import { getUserFullName } from '@uplift/utils';
import { Appointment, CurrentUser } from '@uplift/api';
import { useAssetQuery, useSessionByAppointmentIdQuery } from '@uplift/queries';

export type PreVideoCallPageProps = {
  me: CurrentUser;
  companionRole: string;
  appointment: Appointment;
  onStart: (closeDialog?: boolean) => void;
  blurAvailable: boolean;
};

export const PreVideoCallPage: React.FC<PreVideoCallPageProps> = ({ companionRole, me, appointment, onStart, blurAvailable }) => {
  const formik = useFormik({
    initialValues: {},
    enableReinitialize: true,
    onSubmit: () => onStart()
  });

  const timezone = useTimezone();
  const [ isStartButtonDisabled, setIsStartButtonDisabled ] = useState(true);
  const [ isUnableSessionModalOpen, setIsUnableSessionModalOpen ] = useState(false);
  const [ isAllowDevicesModalOpen, setIsAllowDevicesModalOpen ] = useState(false);
  const { getAudioAndVideoTracks, localTracks, patient, provider } = useVideoContext();
  const { data: session } = useSessionByAppointmentIdQuery(appointment.id, { enabled: !!appointment.id && me.role !== UserRole.patient });

  // Initial fetch of local media tracks
  useEffect(() => {
    const run = async () => {
      try {
        await getAudioAndVideoTracks();

        setIsStartButtonDisabled(false);
      } catch (error) {
        setIsStartButtonDisabled(true);

        console.log('Error acquiring local media:');
        console.dir(error);

        if ([ 'NotAllowedError', 'MicrophonePermissionsDenied', 'CameraPermissionsDenied' ].includes(error?.name)) {
          setIsAllowDevicesModalOpen(true);
        } else {
          setIsUnableSessionModalOpen(true);
        }
      }
    };

    run();
  }, []);

  // Hook for preventing joining a call without an audio device present - however user can join call without camera
  useEffect(() => {
    setIsStartButtonDisabled(localTracks.length === 0 || !localTracks.find(({ kind }) => kind === 'audio'));
  }, [ localTracks ]);


  // USE API For Indication of other participants connected, do not connect / then publish
  // This creates an indication that the participant has joined before they actually have
  // https://www.twilio.com/docs/video/api/participants#participant-list-resource

  const startTime = CalendarDateService.date(appointment?.fromDatetime).tz(timezone).format(TimeFormats.LT);
  const endTime = CalendarDateService.date(appointment?.toDatetime).tz(timezone).format(TimeFormats.LT);
  const party = findParty({ me, patient, provider });
  const userName = getUserFullName(party.user.firstName, party.user.lastName, party.profile?.preferredName);
  const { data: userAvatarAsset } = useAssetQuery(party.profile.avatarAssetId, { enabled: !!party.profile.avatarAssetId });
  const userAvatar = userAvatarAsset?.url;

  if (!appointment) {
    return <Redirect to={ROUTING_CONFIG.main} />;
  }

  return (
    <>
      <FormikProvider value={formik}>
        <Grid container justifyContent="flex-start" alignContent="flex-start">
          <Grid size={{ xs: 12, md: 7 }}>
            <MediaSetupComponent
              blurAvailable={blurAvailable}
              name={userName}
              startTime={startTime}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <InfoComponent
              startButtonDisabled={isStartButtonDisabled}
              onSubmit={onStart}
              userName={userName}
              userAvatar={userAvatar}
              startTime={startTime}
              endTime={endTime}
              companionRole={companionRole}
              sessionNote={session?.sessionNote}
              blurAvailable={blurAvailable}
            />
          </Grid>
        </Grid>
      </FormikProvider>
      <AllowDevicesModal
        isOpen={isAllowDevicesModalOpen}
        setIsOpen={setIsAllowDevicesModalOpen}
      />
      <UnableStartSessionModal
        isOpen={isUnableSessionModalOpen}
      />
      <BetterInPortraitModal />
    </>
  );
};
